import { CountryCode, countryPanelUrl } from './country';

/**
 * Qué panel de país está viendo este navegador y por qué.
 *
 * Vive en `localStorage` y no en la URL a propósito: los dos paneles están en
 * el mismo origen, así que el destino lo lee sin más, y a diferencia de un
 * parámetro de query no se pierde si un proxy o un redirect limpian la URL.
 *
 * El `source` es lo que evita el rebote. Sin distinguir la elección manual de
 * la automática pasaba esto: tocabas "Ver el de Perú", el panel peruano abría
 * el login, la detección veía zona horaria de Uruguay y te devolvía a Uruguay.
 * Una elección explícita gana siempre sobre la detección.
 */
const STORAGE_KEY = 'soyde:country-choice';

/** Ventana en la que una redirección automática todavía explica ESTA visita. */
export const NOTICE_WINDOW_MS = 60_000;

export type CountryChoiceSource = 'auto' | 'manual';

export interface CountryChoice {
  code: CountryCode;
  source: CountryChoiceSource;
  at: number;
}

export const readCountryChoice = (): CountryChoice | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CountryChoice>;
    if (!parsed?.code || typeof parsed.at !== 'number') return null;
    return {
      code: parsed.code,
      source: parsed.source === 'manual' ? 'manual' : 'auto',
      at: parsed.at,
    };
  } catch {
    return null;
  }
};

/**
 * Devuelve false si no se pudo guardar (modo privado, storage bloqueado). Quien
 * llama decide: la redirección automática NO se hace sin poder dejar la marca,
 * porque sin ella el destino volvería a redirigir y el visitante quedaría
 * rebotando entre los dos paneles.
 */
export const rememberCountryChoice = (
  code: CountryCode,
  source: CountryChoiceSource
): boolean => {
  try {
    const choice: CountryChoice = { code, source, at: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    return true;
  } catch {
    return false;
  }
};

/**
 * Manda al usuario al panel de otro país dejando registrado que fue decisión
 * suya. Todo botón o link que cruce de país tiene que pasar por acá: si navega
 * derecho, la detección automática del destino lo devuelve.
 */
export const goToCountryPanel = (code: CountryCode): void => {
  rememberCountryChoice(code, 'manual');
  window.location.href = countryPanelUrl(code);
};
